/**
 * services/axios.ts — single shared axios instance with auth + crypto interceptors.
 *
 * Three cross-cutting concerns live here so every API call inherits them:
 *
 *   1. Authorization header — pulled from the redux session sink.
 *   2. Encrypted envelope — request bodies wrapped, response `Response` field
 *      decrypted in-place, both using the session's `secretKey`.
 *   3. 401 refresh — on an expired access token we hit /auth/refresh ONCE
 *      and retry the original request. Concurrent 401s share the same
 *      in-flight refresh promise (single lock) so we don't burn refresh
 *      tokens. If refresh also fails, we clear the session and bounce.
 *
 * Why a session "sink" instead of importing the redux store directly:
 *   The store imports slices, which import services, which import this
 *   file — a cycle. The sink is bound from `index.tsx` after the store is
 *   built, breaking the cycle.
 */

import axios, {
	AxiosInstance,
	AxiosRequestConfig,
	AxiosResponse,
	InternalAxiosRequestConfig,
} from 'axios';

import { EncryptionService } from './encryption';
import type { ApiEnvelope, RefreshResponse } from '../type/api';
import { logger } from '../helpers/logger';

// ── Session accessor injected from index.tsx ────────────────────────────

export type SessionSnapshot = {
	accessToken: string | null;
	refreshToken: string | null;
	secretKey: string | null;
};

export type SessionSink = {
	get: () => SessionSnapshot;
	set: (next: Partial<SessionSnapshot>) => void;
	clear: () => void;
};

let sessionSink: SessionSink | null = null;

/** Called once from index.tsx after the store is built. */
export function bindSessionSink(sink: SessionSink): void {
	sessionSink = sink;
}

function requireSink(): SessionSink {
	if (!sessionSink) {
		throw new Error(
			'services/axios.ts: session sink not bound. Call bindSessionSink(...) ' +
				'from index.tsx before first request.',
		);
	}
	return sessionSink;
}

// ── Endpoints that send/receive PLAIN JSON (no Secratekey yet) ──────────

const PLAIN_ENDPOINTS = new Set<string>(['auth/login', 'auth/refresh']);

const API_URL = process.env.REACT_APP_API_URL || '/api/v1/';

// ── Axios instance ──────────────────────────────────────────────────────

export const api: AxiosInstance = axios.create({
	baseURL: API_URL,
	timeout: 60000,
	headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor: attach Bearer + encrypt body ────────────────────

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
	const { accessToken, secretKey } = requireSink().get();

	if (accessToken) {
		config.headers.set('Authorization', `Bearer ${accessToken}`);
	}

	const url = (config.url || '').replace(/^\//, '');
	if (
		!PLAIN_ENDPOINTS.has(url) &&
		secretKey &&
		config.data !== undefined &&
		config.data !== null
	) {
		// Skip wrapping if caller already shaped an envelope.
		const alreadyWrapped =
			typeof config.data === 'object' &&
			config.data !== null &&
			'DecryptValue' in (config.data as Record<string, unknown>) &&
			'Secratekey' in (config.data as Record<string, unknown>);
		if (!alreadyWrapped) {
			config.data = EncryptionService.wrapRequest(config.data, secretKey);
		}
	}

	return config;
});

// ── Response interceptor: unwrap envelope + auto-refresh on 401 ──────────

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

/** Promise-lock so concurrent 401s share one refresh round-trip. */
let inflightRefresh: Promise<SessionSnapshot | null> | null = null;

api.interceptors.response.use(
	(response: AxiosResponse<ApiEnvelope<unknown>>) => maybeDecryptEnvelope(response),
	async (error) => {
		const original = (error.config || {}) as RetriableConfig;
		const status = error.response?.status;
		const url = (original.url || '').replace(/^\//, '');

		if (
			status === 401 &&
			!original._retry &&
			!PLAIN_ENDPOINTS.has(url)
		) {
			original._retry = true;
			const refreshed = await sharedRefresh();
			if (refreshed && refreshed.accessToken) {
				original.headers?.set?.('Authorization', `Bearer ${refreshed.accessToken}`);
				return api(original);
			}
			requireSink().clear();
			if (typeof window !== 'undefined') {
				window.location.assign('/login');
			}
		}
		return Promise.reject(error);
	},
);

function maybeDecryptEnvelope(
	response: AxiosResponse<ApiEnvelope<unknown>>,
): AxiosResponse<ApiEnvelope<unknown>> {
	const body = response.data;
	if (
		body &&
		typeof body === 'object' &&
		'StatusCode' in body &&
		'Response' in body &&
		typeof body.Response === 'string' &&
		/^[a-f0-9]+$/i.test(body.Response)
	) {
		const { secretKey } = requireSink().get();
		if (!secretKey) {
			// No key on this client — nothing we can do, but the caller will see
			// a string-shaped Response and get a misleading parse error downstream
			// unless we shout. This usually means the login-response field name
			// drifted (wire is `secratekey`, not `secretKey`).
			logger.error(
				'maybeDecryptEnvelope: encrypted Response received but session has no secretKey. ' +
					'Check that loginThunk maps the wire field `secratekey` into state.secretKey.',
			);
		} else {
			const decoded = EncryptionService.decryptPayload<unknown>(body.Response, secretKey);
			if (decoded !== null) {
				body.Response = decoded;
			} else {
				logger.error(
					'maybeDecryptEnvelope: decrypt returned null. Likely causes: ' +
						'(1) REACT_APP_ENCRYPTION_MASTER_KEY does not match the API .env value, ' +
						'(2) secretKey was not the one issued at login, ' +
						'(3) ciphertext was tampered with in flight.',
				);
			}
		}
	}
	return response;
}

/** Single-flight refresh — concurrent callers await the same promise. */
function sharedRefresh(): Promise<SessionSnapshot | null> {
	if (inflightRefresh) return inflightRefresh;
	inflightRefresh = doRefresh().finally(() => {
		inflightRefresh = null;
	});
	return inflightRefresh;
}

async function doRefresh(): Promise<SessionSnapshot | null> {
	const sink = requireSink();
	const { refreshToken } = sink.get();
	if (!refreshToken) return null;

	try {
		// Bypass the interceptors with a raw axios — they would try to wrap
		// a request that has no secretKey, corrupting the body.
		const raw = axios.create({ baseURL: API_URL, timeout: 15000 });
		const res = await raw.post<ApiEnvelope<RefreshResponse>>('auth/refresh', { refreshToken });
		const envelope = res.data;
		if (
			envelope.StatusCode !== '01' ||
			typeof envelope.Response !== 'object' ||
			envelope.Response === null
		) {
			return null;
		}
		const next = envelope.Response as RefreshResponse;
		sink.set({
			accessToken: next.accessToken,
			refreshToken: next.refreshToken,
			secretKey: next.secratekey,
		});
		return sink.get();
	} catch (err) {
		logger.error('refresh failed', err);
		return null;
	}
}

// ── Convenience wrappers ────────────────────────────────────────────────

export async function getJson<T = unknown>(path: string, config?: AxiosRequestConfig): Promise<T> {
	const res = await api.get<ApiEnvelope<T>>(path, config);
	return unwrap<T>(res.data, path);
}

export async function postJson<T = unknown>(
	path: string,
	body?: unknown,
	config?: AxiosRequestConfig,
): Promise<T> {
	const res = await api.post<ApiEnvelope<T>>(path, body, config);
	return unwrap<T>(res.data, path);
}

function unwrap<T>(envelope: ApiEnvelope<T>, path: string): T {
	if (!envelope) {
		throw new Error(`${path}: empty response`);
	}
	if (envelope.StatusCode !== '01') {
		const msg = typeof envelope.Response === 'string' ? envelope.Response : 'Request failed';
		const err = new Error(`${path}: ${msg}`) as Error & { statusCode?: string };
		err.statusCode = envelope.StatusCode;
		throw err;
	}
	return envelope.Response as T;
}
